import React, { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";


import LookupSelector from "./helpers/LookupSelector";

import { FIELD_TYPES_BY_FIELD_TYPE } from "../lib/constants";
import { prepareSheetSchema } from "../lib/utils";
import Progress from "../Progress";

const LookupsTaskAction = ({
  items = [],
  onRequestParams,
  onComplete,
  //
  assistantStore,
  component
}) => {
  const [manualLookups, setManualLookups] = useState([]);
  const [createdLookups, setCreatedLookups] = useState([]);
  const [progress, setProgress] = useState({label: "Adding lookups (references)"})

  const handleComplete = async (...params) => {
    typeof onComplete === "function" && (await onComplete(...params));
  };

  const addLookupFields = async (lookups) => {
    const lookupsBySheetId = {};
    // group all lookups by target sheetId
    for (const lookup of lookups) {
      lookupsBySheetId[lookup.sheetId] = lookup;
    }

    for (const sheetId in lookupsBySheetId) {
      const { fields, parentSheetId, sheetName } = lookupsBySheetId[sheetId];

      const { list_name, columns } = prepareSheetSchema({
        fields,
        sheetName: sheetName,
      });

      await assistantStore.addColumnsToSheet({
        columns: columns.map((column) => {
          column.column_id = 0;
          column.options = JSON.stringify([column.options]);
          return column;
        }),
        list_name,
        list_id: sheetId,
        parent_id: parentSheetId,
      });
    }

    return lookups;
  };

  const handleOnLoad = async () => {
    try {
      // check for automated lookup
      // else render manual lookup selector
      const manualLookups = [];
      const autoLookups = [];

      const finalItems =
        (typeof onRequestParams === "function" && (await onRequestParams())) ||
        items;

      // load sheets
      await assistantStore.fetchAndLoadSheets();

      for (const item of finalItems) {
        const { fields, ...restItem } = item;

        const manualLookupFields = [];
        const autoLookupFields = [];

        for (const field of fields) {
          const { attributes = {} } = field;
          if (!attributes.source_sheet_name || !attributes.source_column_name) {
            manualLookupFields.push(field);
            continue;
          }

          // check if the sheet exists
          const srcSheet = assistantStore.getSheetByName(
            attributes.source_sheet_name
          );

          // if src sheet doesn't exist
          // add it to manual lookup configuration
          if (!srcSheet) {
            manualLookupFields.push(field);
            continue;
          }

          await assistantStore.fetchAndLoadSheetConfig(srcSheet.id);

          const srcField = assistantStore.getColumnByName(
            attributes.source_column_name,
            srcSheet.id
          );

          // if src field doesn't exist
          // add it to manual lookup configuration
          if (!srcField) {
            manualLookupFields.push(field);
            continue;
          }

          autoLookupFields.push({
            ...field,
            attributes: {
              source_list_id: srcSheet.id,
              source_column_id: srcField.id,
              column_type:
                FIELD_TYPES_BY_FIELD_TYPE[srcField.column_type_id] || "text",
            },
          });
        }

        if (manualLookupFields.length && component !== "ChatDialog") {
          manualLookups.push({ ...restItem, fields: manualLookupFields });
        }

        if (autoLookupFields.length) {
          autoLookups.push({ ...restItem, fields: autoLookupFields });
        }
      }

      const lookups = await addLookupFields(autoLookups);
      setCreatedLookups(lookups);

      if (component === "ChatDialog") {
        await handleComplete(lookups);
      } else {
        if (!manualLookups.length) {
          await handleComplete(lookups);
        } else {
          setManualLookups(manualLookups);
        }
      }
    } catch (e) {
      console.log(`e->`, e);
    }
  };

  const handleCancelManualLookups = async () => {
    try {
      await handleComplete(createdLookups);
    } catch (e) {
      console.log(`e->`, e);
    }
  };

  const handleSubmitManualLookups = async (lookups) => {
    try {
      const newLookups = await addLookupFields(lookups);
      setCreatedLookups((pValue) => [...pValue, ...newLookups]);
      await handleComplete([...createdLookups, ...newLookups]);
    } catch (e) {
      console.log(`e->`, e);
    }
  };

  const renderLookupsTaskAction = () => {
    if (manualLookups.length) {
      return (
        <LookupSelector
          params={manualLookups}
          onCancel={handleCancelManualLookups}
          onSubmit={handleSubmitManualLookups}
        />
      );
    }

    return (
      <Progress progress={progress} parentComponent={component}/>
    );
  };

  useEffect(() => {
    handleOnLoad();
  }, []);

  return renderLookupsTaskAction();
};

export default inject(({ store }) => {
  return {
    assistantStore: store.assistantStore,
    chatHelpersStore: store.chatHelpersStore,
  };
})(observer(LookupsTaskAction));
